'use client'
import { useUserUpdateDetail } from "@/hooks/useUserUpdateDetail";
import { Link, usePathname, useRouter } from "@/locales/routing";
import { useUserStore } from "@/store/useUserStore";
import { LightButton } from "@/ui/Button";
import { Lock } from "@/ui/icons/Lock";
import { mergeModalLayerClassNames, useModalLayer } from "@/ui/modalLayer";
import { Modal, ModalBody, ModalContent, useDisclosure } from "@heroui/react";
import { isString } from "lodash";
import { useLocale, useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import { createContext, PropsWithChildren, useContext, useLayoutEffect } from "react";
import { consumeLoginRedirect, redirectToLogin } from "@/util/loginRedirect";




type ConfirmFn = () => void
const Context = createContext<ConfirmFn>(null!)

const whitePage = [
  "/robot",
  /^\/profile\/[a-zA-Z0-9]+$/,
  "/login",
  '/search',
  '/memory',
  '/circles',
]

export const LoginPopowerProvider = ({ children }: PropsWithChildren) => {

  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const layer = useModalLayer(isOpen)
  const t = useTranslations()
  const locale = useLocale()
  const pathname = usePathname()
  const [isLogined, isPending] = useUserStore(state => [state.isLogined, state.isPending])
  const search = useSearchParams()
  const router = useRouter()

  useLayoutEffect(() => {
    if (!isLogined && !isPending) {
      if (pathname === '/' ||
        whitePage.some(pre =>
          isString(pre) ?
            pathname.startsWith(pre) :
            pre.test(pathname))
      ) return

      // 如果是没有登录访问的是 bonus 页面并且带了邀请码 那么跳转到注册页面
      if (pathname.startsWith('/bonus') && search.get('code')) {
        router.replace(`/login/sign-up?inviteCode=${search.get('code')}`)
        return
      }


      // 未登录访问受保护页：直接去登录，并把当前地址放进 redirect
      redirectToLogin(locale)
      return
    } else if (isLogined) {
      // 忘记密码 / 重置密码流程已登录用户也可能需要（如从 profile 改密页跳转过来），
      // 不应被当作"已登录又进登录页"重定向回首页
      if (pathname.startsWith('/login') && !pathname.startsWith('/login/forget')) {
        // 因鉴权失效被踢到登录页的场景：登录成功后跳回原地址（含参数）
        const redirect = consumeLoginRedirect()
        if (redirect) window.location.replace(redirect)
        else router.replace('/')
      }
    }
  })


  const showLoginPopower: ConfirmFn = () => {
    onOpen()
  }
  // 更新登录详情
  useUserUpdateDetail()



  return <Context.Provider value={showLoginPopower}>
    {children}
    <Modal
      isOpen={isOpen}
      classNames={mergeModalLayerClassNames({
        base: "max-w-[400px]",
      }, layer)}
      placement="center"
      onOpenChange={onOpenChange}
      hideCloseButton
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalBody className="py-4 px-8">
              <div className="flex flex-col items-center gap-5">
                <div className="w-12 h-12 rounded-full bg-theme-error/20 flex justify-center items-center">
                  <Lock className="w-6 h-6 [&>path]:stroke-theme-error/80"></Lock>

                </div>
                <div className="text-center leading-10">
                  <p className="text-[16px] text-theme-primary font-bold">{t('common.loginRequired')}</p>
                  <p className="text-[14px] text-theme-primary/70 mt-2">{t('common.loginRequiredTip')}</p>
                </div>

                <LightButton
                  fullWidth
                  as={Link}
                  href="/login"
                  onPress={onClose}
                >{t('header.login')}</LightButton>
              </div>

            </ModalBody>
          </>
        )}
      </ModalContent>
    </Modal>
  </Context.Provider>
}

export const useLoginPopower = () => useContext(Context)